import React, { FC, useCallback, useMemo, useState } from 'react'
import ReactTooltip from 'react-tooltip'
import { FormModal } from 'transfix-ui/components/Modals'
import { TIconColor } from 'transfix-ui/components/SvgIcon/types'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Box } from 'transfix-ui/core'
import { DeleteIcon } from 'transfix-icons'
import { Tooltip } from 'transfix-ui'
import { useUserDetails } from 'utils/hooks'
import { ITonuFragment } from '../CarrierTonu/graphql/CarrierTonuFragment'
import { useRemoveCarrierTonuMutation } from './graphql/RemoveCarrierTonuMutation'
import { useRevokeCarrierTonuApprovalMutation } from './graphql/RevokeCarrierTonuMutation'

interface IModalProps {
  onClose?: () => void
  onConfirm?: () => void
  tonu: ITonuFragment
  /**
   * Show the confirmation modal when loading the component
   * @default: false
   */
  openOnLoad?: boolean
  iconColor?: TIconColor
}

type TActions = 'revoke' | 'delete'

const getTooltipMessage = (isApproved: boolean, canRevoke: boolean): string => {
  if (isApproved && canRevoke) {
    return 'Revoke this Carrier TONU'
  }
  if (isApproved) {
    return 'You do not have the right to revoke this Carrier TONU'
  }
  return 'Delete this Carrier TONU'
}

const isActionnable = (isApproved: boolean, canRevoke: boolean): boolean => {
  if (isApproved && canRevoke) {
    return true
  }
  if (isApproved) {
    return false
  }
  return true
}

export const TonuDeleteButton: FC<IModalProps> = ({
  iconColor,
  onClose,
  onConfirm,
  openOnLoad = false,
  tonu,
}) => {
  const { userPermissions } = useUserDetails()
  const [open, setOpen] = useState<boolean>(openOnLoad)
  const action: TActions = tonu.approvedAt ? 'revoke' : 'delete'
  const { openToast } = useGlobalToast()
  const [revokeApproval] = useRevokeCarrierTonuApprovalMutation({
    refetchQueries: ['ShipmentBounceDetails'],
  })
  const [removeApproval] = useRemoveCarrierTonuMutation({
    refetchQueries: ['ShipmentBounceDetails'],
  })
  const { canRevokeCarrierTonuApproval = false } = userPermissions || {}

  const openHandler = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // Otherwise it would trigger the ExpandableRow
      e.stopPropagation()
      // Not sure if it's because we open a modal and this component still rendered, but tooltip wont go away
      // forcing it to hide
      ReactTooltip.hide()
      if (isActionnable(!!tonu.approvedAt, canRevokeCarrierTonuApproval)) {
        setOpen(true)
      }
    },
    [tonu.approvedAt, canRevokeCarrierTonuApproval]
  )

  const revokeHandler = useCallback(async () => {
    try {
      const response = await revokeApproval({
        variables: { input: { carrierTonuId: tonu.id || '' } },
      })
      const { data } = response || {}
      const { revokeCarrierTonuApproval: queryResponse } = data || {}
      const { errors = [] } = queryResponse || {}

      setOpen(false)

      const hasErrors = errors.length > 0
      const toastMessage = hasErrors ? errors[0].message : 'Carrier TONU revoked'
      const toastType = hasErrors ? 'alert' : 'default'

      openToast({ toastMessage, toastType })
    } catch ({ message }) {
      openToast({ toastMessage: message, toastType: 'default' })
    }
  }, [revokeApproval, tonu.id, openToast, setOpen])

  const removeHandler = useCallback(async () => {
    try {
      const response = await removeApproval({ variables: { id: tonu.id || '' } })
      const { data } = response || {}
      const { removeCarrierTonu: queryResponse } = data || {}
      const { errors = [] } = queryResponse || {}

      setOpen(false)

      const hasErrors = errors.length > 0
      const toastMessage = hasErrors ? errors[0].message : 'Carrier TONU removed'
      const toastType = hasErrors ? 'alert' : 'default'

      openToast({ toastMessage, toastType })
    } catch ({ message }) {
      openToast({ toastMessage: message, toastType: 'default' })
    }
  }, [removeApproval, tonu.id, openToast])

  const confirmHandler = useCallback(async () => {
    if (action === 'revoke') {
      await revokeHandler()
    }

    if (action === 'delete') {
      await removeHandler()
    }

    if (onConfirm) onConfirm()
  }, [onConfirm, revokeHandler, removeHandler, action])

  const closeHandler = useCallback(() => {
    setOpen(false)

    if (onClose) {
      onClose()
    }
  }, [onClose, setOpen])

  const deleteTooltipMessage = useMemo(() => {
    return getTooltipMessage(!!tonu.approvedAt, canRevokeCarrierTonuApproval)
  }, [tonu.approvedAt, canRevokeCarrierTonuApproval])

  return (
    <Box onClick={openHandler}>
      <Tooltip
        id={`delete-carrier-tonu-${tonu.id}`}
        trigger={
          <div>
            <DeleteIcon color={iconColor} size='medium' />
            {open && (
              <FormModal
                size={4}
                open
                onClose={closeHandler}
                title='Are you sure?'
                actions={[
                  {
                    type: 'dismiss',
                    label: 'Cancel',
                    dataTest: 'cancel-button',
                    action: closeHandler,
                  },
                  {
                    type: 'confirm',
                    label: 'Delete',
                    dataTest: 'confirm-button',
                    action: confirmHandler,
                  },
                ]}>
                Are you sure you want to {action} this Carrier TONU?
              </FormModal>
            )}
          </div>
        }>
        {deleteTooltipMessage}
      </Tooltip>
    </Box>
  )
}
